// Port of TrailData/Catalog/TrailCatalog.swift: the bundled trail catalogue — a small index (always loaded), one trail
// pack per trail (loaded on demand) and the world regulatory layer.
//
// Layout under /data/ (public/data, copied from the iOS Fixtures):
// - `catalog.json` → `TrailCatalogIndex`
// - `trails/<packResource>.json` → `TrailPack` (compact geometry)
// - `world_regulations.json` → `TrailDataset` (regulations, coverage, protected areas only)
//
// Every function takes the JSON loader (paths relative to /data/), `fetchDataJSON` by default, so tests and the
// service worker can substitute their own. Decoding a large pack takes tens of milliseconds (PCT, Camino Francés).

import type { TrailCatalogEntry, TrailCatalogIndex, TrailDataset, TrailPack } from '../core/models';
import { decodeCatalogIndex, decodeTrailDataset, decodeTrailPack, packToDataset } from './decode';
import { TrailDataError, errorMessage } from './errors';

/** Reads and parses one JSON file; `path` is relative to /data/ (e.g. 'catalog.json', 'trails/tmb.json'). */
export type DataLoader = (path: string) => Promise<unknown>;

/**
 * In-browser loader: `fetch('/data/<path>')` → parsed JSON. Throws `TrailDataError`: `fixtureMissing(path)` on
 * HTTP 404, `decoding(path, …)` when the request fails (offline and not cached), on other HTTP errors and on invalid
 * JSON.
 */
export async function fetchDataJSON(path: string): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(`/data/${path}`);
  } catch (e) {
    throw TrailDataError.decoding(path, `unreadable: ${errorMessage(e)}`);
  }
  if (response.status === 404) throw TrailDataError.fixtureMissing(path);
  if (!response.ok) throw TrailDataError.decoding(path, `unreadable: HTTP ${response.status}`);
  try {
    return await response.json();
  } catch (e) {
    throw TrailDataError.decoding(path, `invalid JSON: ${errorMessage(e)}`);
  }
}

/** Swift `TrailCatalog.maxCachedPacks`. */
export const maxCachedPacks = 4;

/** Decoded packs per loader, least recently used first (a Map keeps insertion order). */
const packCaches = new WeakMap<DataLoader, Map<string, Promise<TrailPack>>>();

function packCache(loader: DataLoader): Map<string, Promise<TrailPack>> {
  let cache = packCaches.get(loader);
  if (!cache) {
    cache = new Map();
    packCaches.set(loader, cache);
  }
  return cache;
}

/** Loads the catalogue index. Returns an empty index when the file is missing or undecodable (never throws). */
export async function loadIndex(loader: DataLoader = fetchDataJSON): Promise<TrailCatalogIndex> {
  try {
    return decodeCatalogIndex(await loader('catalog.json'));
  } catch (e) {
    console.error(`catalog.json could not be loaded: ${errorMessage(e)}`);
    return { formatVersion: 1, generatedAt: 0, entries: [] };
  }
}

/** Resource name of an entry's pack (`packResource`, else the entry id). */
function resourceOf(entryOrResource: TrailCatalogEntry | string): string {
  return typeof entryOrResource === 'string' ? entryOrResource : (entryOrResource.packResource ?? entryOrResource.id);
}

async function readPack(resource: string, loader: DataLoader): Promise<TrailPack> {
  const file = `trails/${resource}.json`;
  let json: unknown;
  try {
    json = await loader(file);
  } catch (e) {
    if (e instanceof TrailDataError) throw e;
    throw TrailDataError.decoding(file, `unreadable: ${errorMessage(e)}`);
  }
  try {
    return decodeTrailPack(json);
  } catch (e) {
    throw TrailDataError.decoding(file, errorMessage(e));
  }
}

/**
 * Loads (and caches, at most `maxCachedPacks`, least recently used evicted first) a trail pack by resource name or
 * catalogue entry. Concurrent calls share one load. Throws `TrailDataError` (`fixtureMissing`, `decoding`).
 */
export async function loadPack(
  entryOrResource: TrailCatalogEntry | string,
  loader: DataLoader = fetchDataJSON,
): Promise<TrailPack> {
  const resource = resourceOf(entryOrResource);
  const cache = packCache(loader);
  let pending = cache.get(resource);
  if (pending) {
    cache.delete(resource); // re-insert: most recently used
    cache.set(resource, pending);
    return pending;
  }
  pending = readPack(resource, loader);
  cache.set(resource, pending);
  while (cache.size > maxCachedPacks) {
    const oldest = cache.keys().next().value as string;
    cache.delete(oldest);
  }
  pending.catch(() => {
    if (cache.get(resource) === pending) cache.delete(resource); // failures are not cached
  });
  return pending;
}

/**
 * Full dataset of a catalogue trail; undefined when the entry has no pack (its trail lives in another dataset, e.g.
 * the demo). Throws `TrailDataError`.
 */
export async function loadDataset(
  entry: TrailCatalogEntry,
  loader: DataLoader = fetchDataJSON,
): Promise<TrailDataset | undefined> {
  if (entry.packResource === undefined) return undefined;
  return packToDataset(await loadPack(entry.packResource, loader));
}

/** World regulatory layer (country/regional rules, coverage, official park boundaries); undefined when unavailable. */
export async function loadWorldRegulatoryLayer(loader: DataLoader = fetchDataJSON): Promise<TrailDataset | undefined> {
  try {
    return decodeTrailDataset(await loader('world_regulations.json'));
  } catch (e) {
    console.error(`world_regulations.json could not be loaded: ${errorMessage(e)}`);
    return undefined;
  }
}

/** Clears the pack cache of `loader` (all loaders when omitted is impossible with a WeakMap: pass the loader used). */
export function purgeCache(loader: DataLoader = fetchDataJSON): void {
  packCaches.get(loader)?.clear();
}
